import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "../lib/api";
import { Package, X, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

// Message par défaut rassurant (affiché si l'admin n'a pas customisé le texte en DB)
const DEFAULT_BANNER_MSG = "La livraison de matériel physique vers la France et l'Europe est momentanément suspendue. Toutefois, notre boutique reste 100% ouverte ! Profitez de nos Ressources PDF et de nos Applications accessibles en téléchargement immédiat. ✨";

// Union européenne (27 États-membres) — ISO 3166-1 alpha-2
const EU_COUNTRIES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
]);

const SalesStatusContext = createContext({
  physical_sales_disabled: false,
  physical_sales_banner: "",
  country: null,
  is_fr_eu_customer: false,
  should_block_physical: false,
  showBlockingModal: () => {},
});

export function SalesStatusProvider({ children }) {
  const [status, setStatus] = useState({ physical_sales_disabled: false, physical_sales_banner: "" });
  const [country, setCountry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    // 1. Settings (flag + message)
    api.get("/settings").then((r) => {
      if (!mounted) return;
      setStatus({
        physical_sales_disabled: !!r.data?.physical_sales_disabled,
        physical_sales_banner: r.data?.physical_sales_banner || "",
      });
    }).catch(() => {});
    // 2. Détection du pays (localStorage cache + geoip)
    const cached = typeof window !== "undefined" ? window.localStorage.getItem("detected_country") : null;
    if (cached) setCountry(cached);
    else {
      fetch("https://api.country.is", { method: "GET" })
        .then((r) => r.json())
        .then((data) => {
          if (!mounted || !data?.country) return;
          setCountry(data.country);
          try { window.localStorage.setItem("detected_country", data.country); } catch {}
        })
        .catch(() => { /* on suppose FR par défaut (bloqué) si aucun signal */ if (mounted) setCountry("FR"); });
    }
    return () => { mounted = false; };
  }, []);

  const is_fr_eu_customer = !country || EU_COUNTRIES.has(country);  // Par précaution, on bloque tant qu'on n'a pas confirmé un pays HORS UE
  const should_block_physical = status.physical_sales_disabled && is_fr_eu_customer;

  const showBlockingModal = useCallback(() => setModalOpen(true), []);
  const value = { ...status, country, is_fr_eu_customer, should_block_physical, showBlockingModal };

  return (
    <SalesStatusContext.Provider value={value}>
      {children}
      {modalOpen && (
        <PhysicalSalesBlockingModal
          message={status.physical_sales_banner}
          onClose={() => setModalOpen(false)}
        />
      )}
    </SalesStatusContext.Provider>
  );
}

export function useSalesStatus() { return useContext(SalesStatusContext); }

/**
 * Modal plein écran affichée quand un client FR/UE tente d'ajouter un article
 * physique au panier alors que la vente physique est suspendue.
 * Empêche l'ajout et redirige mentalement vers les ressources digitales.
 */
function PhysicalSalesBlockingModal({ message, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="physical-sales-blocking-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-sky-500 to-sky-600 text-white p-5 flex items-start gap-3">
          <Package size={26} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-display text-xl font-semibold">Information livraison</h2>
            <p className="text-sm mt-1 text-white/90">Matériel physique · France & Union européenne</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full" aria-label="Fermer">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-ink leading-relaxed" data-testid="physical-sales-modal-message">
            {message || DEFAULT_BANNER_MSG}
          </p>
          <div className="bg-sage/10 border border-sage/30 rounded-2xl p-3 text-sm inline-flex items-start gap-2">
            <Sparkles size={16} className="flex-shrink-0 mt-0.5 text-sage-dark" />
            <span>Nos <b>Ressources PDF</b> et notre <b>Application</b> FlashRead Kids restent disponibles en téléchargement immédiat.</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-ink/5 text-ink font-medium hover:bg-ink/10"
              data-testid="physical-sales-modal-close"
            >
              <ArrowLeft size={16} /> Retour au catalogue
            </button>
            <a
              href="/ressources"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-sky-600 text-white font-semibold hover:bg-sky-700 no-underline shadow-sm"
              data-testid="physical-sales-modal-goto-resources"
            >
              Voir les ressources digitales <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Bandeau plein-largeur ambre (top de page) OU encart inline (dans le panier).
 * Affiché uniquement pour les clients FR / UE quand le flag est activé.
 */
export default function PhysicalSalesBanner({ variant = "banner", dismissable = false }) {
  const { should_block_physical, physical_sales_banner } = useSalesStatus();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissable && typeof window !== "undefined") {
      setDismissed(window.sessionStorage.getItem("phys_sales_banner_dismissed") === "1");
    }
  }, [dismissable]);

  if (!should_block_physical || dismissed) return null;

  // Le texte DB peut être vide ou ancien → on affiche le message rassurant par défaut
  const message = physical_sales_banner && physical_sales_banner.length > 20
    ? physical_sales_banner
    : DEFAULT_BANNER_MSG;

  function dismiss() {
    setDismissed(true);
    try { window.sessionStorage.setItem("phys_sales_banner_dismissed", "1"); } catch {}
  }

  if (variant === "inline") {
    return (
      <div
        data-testid="physical-sales-banner-inline"
        className="rounded-2xl bg-sky-50 border border-sky-200 p-4 text-sky-900 flex items-start gap-3"
      >
        <Package size={20} className="flex-shrink-0 mt-0.5 text-sky-600" />
        <div className="text-sm leading-relaxed flex-1">
          <p className="font-semibold mb-1">📦 Information livraison — France & UE</p>
          <p className="text-[13px] text-sky-800/90">{message}</p>
          <a
            href="/ressources"
            className="mt-2 inline-flex items-center gap-1 text-sky-700 font-semibold text-[13px] hover:underline no-underline"
            data-testid="physical-sales-inline-cta"
          >
            Voir les ressources digitales <ArrowRight size={14} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="physical-sales-banner"
      className="bg-sky-50 border-b border-sky-200 text-sky-900 px-4 py-2 relative"
      role="status"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 pr-8 flex-wrap sm:flex-nowrap">
        <Package size={16} className="flex-shrink-0 text-sky-600" />
        <p className="text-[13px] leading-snug flex-1 min-w-0">
          <span className="font-semibold">Info livraison :</span> {message}
        </p>
        <a
          href="/ressources"
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-white bg-sky-600 hover:bg-sky-700 px-3 py-1 rounded-full whitespace-nowrap no-underline"
          data-testid="physical-sales-banner-cta"
        >
          Voir les ressources digitales <ArrowRight size={12} />
        </a>
      </div>
      {dismissable && (
        <button
          onClick={dismiss}
          data-testid="physical-sales-banner-close"
          className="absolute top-1.5 right-2 p-1 rounded-full hover:bg-sky-100 text-sky-700"
          aria-label="Fermer l'annonce"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
